import { cellRes, cellToLatLng, ringUnitRad } from "./fib-grid";

function haversineDist(lat1, lng1, lat2, lng2) {
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * Math.asin(Math.sqrt(a));
}

// Peak profile: a cusp, not a bell. The height at distance k (ring units)
// from a summit is (1 - k/R)^PEAK_SHARPNESS, with R = 3σ. Its slope is
// steepest at the summit and eases out only towards the ground, so a peak
// only ever gets steeper as it rises and never rounds off into a flat top
// (a Gaussian has zero slope there). Higher sharpness → a needle-like peak.
const PEAK_SHARPNESS = 2;
const falloff = (k, cellSigma) => {
  const t = 1 - k / (3 * cellSigma);
  return t > 0 ? t ** PEAK_SHARPNESS : 0;
};

// "Soft max" falloff over the grid, in its simplest form. For each point with
// non-zero raw altitude, scatter `raw * falloff(k)` outward, where k is the
// geodesic distance in ring units (fib-grid.js ringUnitRad). Compares every
// raw point with every cell: the reference createRingSmoother is checked
// against, not for use at scale.
export function smoothByRing(rawById, sigma, cells) {
  if (sigma <= 0.5) return new Map(rawById);

  const smooth = new Map();
  const MAX_RAW = 0.25;
  const centres = cells.map((c) => cellToLatLng(c));

  for (const [cellId, raw] of rawById) {
    if (!raw) continue;

    const normalizedHeight = Math.min(1.0, raw / MAX_RAW);
    // Blend between fixed and relative size: base size is 50% of max, plus up to 50% based on height
    const cellSigma = Math.max(1.0, sigma * (0.5 + 0.5 * normalizedHeight));
    const kMax = Math.max(1, Math.ceil(3 * cellSigma));
    const ringUnit = ringUnitRad(cellRes(cellId));
    const [cLat, cLng] = cellToLatLng(cellId);

    for (let j = 0; j < cells.length; j++) {
      const continuousK = haversineDist(cLat, cLng, centres[j][0], centres[j][1]) / ringUnit;
      if (continuousK > kMax) continue;
      const v = falloff(continuousK, cellSigma) * raw;
      const cur = smooth.get(cells[j]) || 0;
      if (v > cur) smooth.set(cells[j], v);
    }
  }
  return smooth;
}

const MAX_CACHED_KERNELS = 4000;
const DEG = Math.PI / 180;

// Same falloff as smoothByRing, but fast enough to re-run on every
// timeline scrub step:
//   - neighbourhoods come from a latitude-band index over precomputed cell
//     centres, and are cached per cell;
//   - results go into a Float32Array indexed like `cells`, not a Map.
// The falloff is evaluated on geodesic distance in ring units, exactly as
// smoothByRing does, so the output matches.
export function createRingSmoother(cells) {
  const size = cells.length;
  const index = new Map(cells.map((c, i) => [c, i]));
  const lat = new Float64Array(size);
  const lng = new Float64Array(size);
  const ux = new Float64Array(size); // unit vectors, for cheap distance tests
  const uy = new Float64Array(size);
  const uz = new Float64Array(size);
  cells.forEach((c, i) => {
    [lat[i], lng[i]] = cellToLatLng(c);
    const cl = Math.cos(lat[i] * DEG);
    ux[i] = cl * Math.cos(lng[i] * DEG);
    uy[i] = cl * Math.sin(lng[i] * DEG);
    uz[i] = Math.sin(lat[i] * DEG);
  });

  // 1° latitude bands, each sorted by longitude for binary search.
  const bandMembers = Array.from({ length: 180 }, () => []);
  for (let i = 0; i < size; i++) bandMembers[Math.min(179, Math.floor(lat[i] + 90))].push(i);
  const bands = bandMembers.map((members) => {
    members.sort((a, b) => lng[a] - lng[b]);
    return { idx: Int32Array.from(members), lng: Float64Array.from(members, (i) => lng[i]) };
  });
  const lowerBound = (arr, v) => {
    let lo = 0, hi = arr.length;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (arr[mid] < v) lo = mid + 1; else hi = mid; }
    return lo;
  };

  // Calls visit(j, distRad) for every cell within radiusRad of cell i.
  const forEachWithin = (i, radiusRad, visit) => {
    const rDeg = radiusRad / DEG;
    const b0 = Math.max(0, Math.floor(lat[i] + 90 - rDeg));
    const b1 = Math.min(179, Math.floor(lat[i] + 90 + rDeg));
    const minDot = Math.cos(radiusRad) - 1e-9;
    const scan = (band, from, to) => {
      for (let k = from; k < to; k++) {
        const j = band.idx[k];
        if (ux[i] * ux[j] + uy[i] * uy[j] + uz[i] * uz[j] < minDot) continue;
        const d = haversineDist(lat[i], lng[i], lat[j], lng[j]);
        if (d <= radiusRad) visit(j, d);
      }
    };
    for (let b = b0; b <= b1; b++) {
      const band = bands[b];
      const maxAbsLat = Math.max(Math.abs(b - 90), Math.abs(b - 89));
      const cosLat = Math.cos(Math.min(89.9, maxAbsLat) * DEG);
      const dLng = (rDeg / cosLat) * 1.1 + 0.5;
      if (dLng >= 180) { scan(band, 0, band.idx.length); continue; }
      const lo = lng[i] - dLng;
      const hi = lng[i] + dLng;
      scan(band, lowerBound(band.lng, Math.max(-180, lo)), lowerBound(band.lng, Math.min(180, hi) + 1e-9));
      if (lo < -180) scan(band, lowerBound(band.lng, lo + 360), band.idx.length);
      if (hi > 180) scan(band, 0, lowerBound(band.lng, hi - 360 + 1e-9));
    }
  };

  const kernels = new Map(); // `${cellIdx}:${sigma}` → { idx: Int32Array, k: Float32Array }
  const kernelFor = (i, cellId, sigma) => {
    const key = `${i}:${sigma}`;
    let kernel = kernels.get(key);
    if (kernel) return kernel;

    const ring1Dist = ringUnitRad(cellRes(cellId));

    // Widest neighbourhood any height can need (normalizedHeight = 1).
    const kMax = Math.max(1, Math.ceil(3 * Math.max(1.0, sigma)));
    const idx = [];
    const ks = [];
    forEachWithin(i, kMax * ring1Dist, (j, d) => { idx.push(j); ks.push(d / ring1Dist); });
    kernel = { idx: Int32Array.from(idx), k: Float32Array.from(ks) };
    kernels.set(key, kernel);
    if (kernels.size > MAX_CACHED_KERNELS) kernels.delete(kernels.keys().next().value);
    return kernel;
  };

  return (rawById, sigma) => {
    const out = new Float32Array(size);
    const MAX_RAW = 0.25;
    for (const [cellId, raw] of rawById) {
      const i = index.get(cellId);
      if (!raw || i === undefined) continue;
      if (sigma <= 0.5) {
        out[i] = Math.max(out[i], raw);
        continue;
      }
      const normalizedHeight = Math.min(1.0, raw / MAX_RAW);
      const cellSigma = Math.max(1.0, sigma * (0.5 + 0.5 * normalizedHeight));
      const kMax = Math.max(1, Math.ceil(3 * cellSigma));
      const { idx, k } = kernelFor(i, cellId, sigma);
      for (let j = 0; j < idx.length; j++) {
        const kk = k[j];
        if (kk > kMax) continue;
        const v = falloff(kk, cellSigma) * raw;
        if (v > out[idx[j]]) out[idx[j]] = v;
      }
    }
    return out;
  };
}
